/**
 * Mapeo de provincias del locale a sus variantes en la base de datos
 */

export const PROVINCIA_MAPPING = {
  // ANDALUCÍA
  "Almería": ["Almería"],
  "Cádiz": ["Cádiz"],
  "Córdoba": ["Córdoba"],
  "Granada": ["Granada"],
  "Huelva": ["Huelva"],
  "Jaén": ["Jaén"],
  "Málaga": ["Málaga"],
  "Sevilla": ["Sevilla"],

  // ARAGÓN
  "Huesca": ["Huesca"],
  "Teruel": ["Teruel"],
  "Zaragoza": ["Zaragoza"],

  // PRINCIPADO DE ASTURIAS
  "Asturias": ["Asturias", "Oviedo"], // Oviedo a veces aparece como provincia

  // ILLES BALEARS
  "Illes Balears": ["Illes Balears", "Islas Baleares", "Baleares"],

  // CANARIAS
  "Las Palmas": ["Las Palmas"],
  "Santa Cruz de Tenerife": ["Santa Cruz de Tenerife", "Tenerife"],

  // CANTABRIA
  "Cantabria": ["Cantabria"],

  // CASTILLA Y LEÓN
  "Ávila": ["Ávila"],
  "Burgos": ["Burgos"],
  "León": ["León"],
  "Palencia": ["Palencia"],
  "Salamanca": ["Salamanca"],
  "Segovia": ["Segovia"],
  "Soria": ["Soria"],
  "Valladolid": ["Valladolid"],
  "Zamora": ["Zamora"],

  // CASTILLA-LA MANCHA
  "Albacete": ["Albacete"],
  "Ciudad Real": ["Ciudad Real"],
  "Cuenca": ["Cuenca"],
  "Guadalajara": ["Guadalajara"],
  "Toledo": ["Toledo"],

  // CATALUÑA
  "Barcelona": ["Barcelona"],
  "Girona": ["Girona", "Gerona"],
  "Lleida": ["Lleida", "Lérida"],
  "Tarragona": ["Tarragona"],

  // COMUNITAT VALENCIANA
  "Alicante/Alacant": ["Alicante", "Alacant", "Alicante/Alacant", "Alacant/Alicante"],
  "Castellón/Castelló": ["Castellón", "Castelló", "Castellón/Castelló", "Castelló/Castellón"],
  "Valencia/València": ["Valencia", "València", "Valencia/València", "València/Valencia"],

  // EXTREMADURA
  "Badajoz": ["Badajoz"],
  "Cáceres": ["Cáceres"],

  // GALICIA
  "A Coruña": ["A Coruña", "La Coruña", "Coruña"],
  "Lugo": ["Lugo"],
  "Ourense": ["Ourense", "Orense"],
  "Pontevedra": ["Pontevedra"],

  // COMUNIDAD DE MADRID
  "Madrid": ["Madrid"],

  // REGIÓN DE MURCIA
  "Murcia": ["Murcia", "Región de Murcia"],

  // COMUNIDAD FORAL DE NAVARRA
  "Navarra": ["Navarra", "Comunidad Foral de Navarra", "Nafarroa"],

  // PAÍS VASCO
  "Araba/Álava": ["Álava", "Araba", "Araba/Álava"],
  "Bizkaia": ["Bizkaia", "Vizcaya"],
  "Gipuzkoa": ["Gipuzkoa", "Guipúzcoa"],

  // LA RIOJA
  "La Rioja": ["La Rioja"],

  // CEUTA Y MELILLA
  "Ceuta": ["Ceuta"],
  "Melilla": ["Melilla"],
};

/**
 * Obtiene todas las variantes de una provincia para consultas a la BBDD
 */
export function getProvinciaVariants(provinciaLocale) {
  if (!provinciaLocale) return [];
  // Match directo por key
  if (PROVINCIA_MAPPING[provinciaLocale]) {
    return PROVINCIA_MAPPING[provinciaLocale];
  }
  // Buscar dentro de las variantes (getProvinces() usa nombres simples
  // como "Alicante" pero la key es "Alicante/Alacant")
  for (const variants of Object.values(PROVINCIA_MAPPING)) {
    if (variants.includes(provinciaLocale)) {
      return variants;
    }
  }
  return [provinciaLocale];
}

/**
 * Obtiene la primera variante (canónica) de una provincia
 */
export function getCanonicalProvincia(provinciaLocale) {
  const variants = getProvinciaVariants(provinciaLocale);
  return variants[0] || provinciaLocale;
}

/**
 * Verifica si una provincia tiene múltiples variantes
 */
export function hasMultipleVariants(provinciaLocale) {
  return getProvinciaVariants(provinciaLocale).length > 1;
}

/**
 * Normaliza un nombre de provincia de la BBDD al formato del locale
 */
export function normalizeProvinciaFromBBDD(provinciaBBDD) {
  for (const [locale, variants] of Object.entries(PROVINCIA_MAPPING)) {
    if (variants.includes(provinciaBBDD)) {
      return locale;
    }
  }
  return null;
}

/**
 * Obtiene todas las provincias disponibles en el mapping
 * 
 * @returns {string[]} Array con todas las provincias del locale
 */
export function getAllProvincias() {
  return Object.keys(PROVINCIA_MAPPING);
}

/**
 * Obtiene el número total de provincias
 *
 * @returns {number} Total de provincias
 */
export function getTotalProvincias() {
  return Object.keys(PROVINCIA_MAPPING).length;
}

/**
 * Mapa de nombre de provincia (locale key) → código INE (2 dígitos)
 */
export const PROVINCIA_TO_CODE = {
  "Almería": "04",
  "Cádiz": "11",
  "Córdoba": "14",
  "Granada": "18",
  "Huelva": "21",
  "Jaén": "23",
  "Málaga": "29",
  "Sevilla": "41",
  "Huesca": "22",
  "Teruel": "44",
  "Zaragoza": "50",
  "Asturias": "33",
  "Illes Balears": "07",
  "Las Palmas": "35",
  "Santa Cruz de Tenerife": "38",
  "Cantabria": "39",
  "Ávila": "05",
  "Burgos": "09",
  "León": "24",
  "Palencia": "34",
  "Salamanca": "37",
  "Segovia": "40",
  "Soria": "42",
  "Valladolid": "47",
  "Zamora": "49",
  "Albacete": "02",
  "Ciudad Real": "13",
  "Cuenca": "16",
  "Guadalajara": "19",
  "Toledo": "45",
  "Barcelona": "08",
  "Girona": "17",
  "Lleida": "25",
  "Tarragona": "43",
  "Alicante/Alacant": "03",
  "Castellón/Castelló": "12",
  "Valencia/València": "46",
  "Badajoz": "06",
  "Cáceres": "10",
  "A Coruña": "15",
  "Lugo": "27",
  "Ourense": "32",
  "Pontevedra": "36",
  "Madrid": "28",
  "Murcia": "30",
  "Navarra": "31",
  "Araba/Álava": "01",
  "Bizkaia": "48",
  "Gipuzkoa": "20",
  "La Rioja": "26",
  "Ceuta": "51",
  "Melilla": "52",
};

/**
 * Obtiene el código INE de provincia a partir de su nombre (locale key o variante)
 * @param {string} name - Nombre de la provincia
 * @returns {string|null} Código INE de 2 dígitos o null
 */
export function getProvinciaCode(name) {
  if (!name) return null;
  // Match directo por key
  if (PROVINCIA_TO_CODE[name]) return PROVINCIA_TO_CODE[name];
  // Buscar en PROVINCIA_MAPPING: si el nombre es una variante, encontrar la key
  for (const [key, variants] of Object.entries(PROVINCIA_MAPPING)) {
    if (variants.includes(name) && PROVINCIA_TO_CODE[key]) {
      return PROVINCIA_TO_CODE[key];
    }
  }
  return null;
}

/**
 * Capitales de provincia de España
 * Formato: { slug: { nombre, provincia, lat, lon } }
 * El slug es la versión URL-friendly del nombre
 */
export const CAPITALES_PROVINCIA = {
  // ANDALUCÍA
  "almeria": { nombre: "Almería", provincia: "Almería", lat: 36.8340, lon: -2.4637 },
  "cadiz": { nombre: "Cádiz", provincia: "Cádiz", lat: 36.5271, lon: -6.2886 },
  "cordoba": { nombre: "Córdoba", provincia: "Córdoba", lat: 37.8882, lon: -4.7794 },
  "granada": { nombre: "Granada", provincia: "Granada", lat: 37.1773, lon: -3.5986 },
  "huelva": { nombre: "Huelva", provincia: "Huelva", lat: 37.2614, lon: -6.9447 },
  "jaen": { nombre: "Jaén", provincia: "Jaén", lat: 37.7796, lon: -3.7849 },
  "malaga": { nombre: "Málaga", provincia: "Málaga", lat: 36.7213, lon: -4.4214 },
  "sevilla": { nombre: "Sevilla", provincia: "Sevilla", lat: 37.3891, lon: -5.9845 },

  // ARAGÓN
  "huesca": { nombre: "Huesca", provincia: "Huesca", lat: 42.1401, lon: -0.4089 },
  "teruel": { nombre: "Teruel", provincia: "Teruel", lat: 40.3456, lon: -1.1065 },
  "zaragoza": { nombre: "Zaragoza", provincia: "Zaragoza", lat: 41.6488, lon: -0.8891 },

  // PRINCIPADO DE ASTURIAS
  "oviedo": { nombre: "Oviedo", provincia: "Asturias", lat: 43.3614, lon: -5.8494 },

  // ILLES BALEARS
  "palma": { nombre: "Palma", provincia: "Illes Balears", lat: 39.5696, lon: 2.6502 },

  // CANARIAS
  "las-palmas-de-gran-canaria": { nombre: "Las Palmas de Gran Canaria", provincia: "Las Palmas", lat: 28.1235, lon: -15.4363 },
  "santa-cruz-de-tenerife": { nombre: "Santa Cruz de Tenerife", provincia: "Santa Cruz de Tenerife", lat: 28.4636, lon: -16.2518 },

  // CANTABRIA
  "santander": { nombre: "Santander", provincia: "Cantabria", lat: 43.4623, lon: -3.8100 },

  // CASTILLA Y LEÓN
  "avila": { nombre: "Ávila", provincia: "Ávila", lat: 40.6566, lon: -4.7000 },
  "burgos": { nombre: "Burgos", provincia: "Burgos", lat: 42.3439, lon: -3.6969 },
  "leon": { nombre: "León", provincia: "León", lat: 42.5987, lon: -5.5671 },
  "palencia": { nombre: "Palencia", provincia: "Palencia", lat: 42.0095, lon: -4.5288 },
  "salamanca": { nombre: "Salamanca", provincia: "Salamanca", lat: 40.9701, lon: -5.6635 },
  "segovia": { nombre: "Segovia", provincia: "Segovia", lat: 40.9429, lon: -4.1088 },
  "soria": { nombre: "Soria", provincia: "Soria", lat: 41.7636, lon: -2.4649 },
  "valladolid": { nombre: "Valladolid", provincia: "Valladolid", lat: 41.6523, lon: -4.7245 },
  "zamora": { nombre: "Zamora", provincia: "Zamora", lat: 41.5034, lon: -5.7467 },

  // CASTILLA-LA MANCHA
  "albacete": { nombre: "Albacete", provincia: "Albacete", lat: 38.9942, lon: -1.8585 },
  "ciudad-real": { nombre: "Ciudad Real", provincia: "Ciudad Real", lat: 38.9860, lon: -3.9290 },
  "cuenca": { nombre: "Cuenca", provincia: "Cuenca", lat: 40.0704, lon: -2.1374 },
  "guadalajara": { nombre: "Guadalajara", provincia: "Guadalajara", lat: 40.6333, lon: -3.1667 },
  "toledo": { nombre: "Toledo", provincia: "Toledo", lat: 39.8628, lon: -4.0273 },

  // CATALUÑA
  "barcelona": { nombre: "Barcelona", provincia: "Barcelona", lat: 41.3851, lon: 2.1734 },
  "girona": { nombre: "Girona", provincia: "Girona", lat: 41.9794, lon: 2.8214 },
  "lleida": { nombre: "Lleida", provincia: "Lleida", lat: 41.6176, lon: 0.6200 },
  "tarragona": { nombre: "Tarragona", provincia: "Tarragona", lat: 41.1189, lon: 1.2445 },

  // COMUNITAT VALENCIANA
  "alicante": { nombre: "Alicante", provincia: "Alicante/Alacant", lat: 38.3452, lon: -0.4810 },
  "castellon": { nombre: "Castellón de la Plana", provincia: "Castellón/Castelló", lat: 39.9864, lon: -0.0513 },
  "valencia": { nombre: "Valencia", provincia: "Valencia/València", lat: 39.4699, lon: -0.3763 },

  // EXTREMADURA
  "badajoz": { nombre: "Badajoz", provincia: "Badajoz", lat: 38.8794, lon: -6.9707 },
  "caceres": { nombre: "Cáceres", provincia: "Cáceres", lat: 39.4753, lon: -6.3724 },

  // GALICIA
  "a-coruna": { nombre: "A Coruña", provincia: "A Coruña", lat: 43.3623, lon: -8.4115 },
  "lugo": { nombre: "Lugo", provincia: "Lugo", lat: 43.0097, lon: -7.5567 },
  "ourense": { nombre: "Ourense", provincia: "Ourense", lat: 42.3364, lon: -7.8638 },
  "pontevedra": { nombre: "Pontevedra", provincia: "Pontevedra", lat: 42.4314, lon: -8.6445 },

  // COMUNIDAD DE MADRID
  "madrid": { nombre: "Madrid", provincia: "Madrid", lat: 40.4168, lon: -3.7038 },

  // REGIÓN DE MURCIA
  "murcia": { nombre: "Murcia", provincia: "Murcia", lat: 37.9922, lon: -1.1307 },

  // COMUNIDAD FORAL DE NAVARRA
  "pamplona": { nombre: "Pamplona", provincia: "Navarra", lat: 42.8125, lon: -1.6458 },

  // PAÍS VASCO
  "vitoria-gasteiz": { nombre: "Vitoria-Gasteiz", provincia: "Araba/Álava", lat: 42.8467, lon: -2.6726 },
  "bilbao": { nombre: "Bilbao", provincia: "Bizkaia", lat: 43.2630, lon: -2.9350 },
  "san-sebastian": { nombre: "San Sebastián", provincia: "Gipuzkoa", lat: 43.3183, lon: -1.9812 },

  // LA RIOJA
  "logrono": { nombre: "Logroño", provincia: "La Rioja", lat: 42.4650, lon: -2.4456 },

  // CEUTA Y MELILLA
  "ceuta": { nombre: "Ceuta", provincia: "Ceuta", lat: 35.8894, lon: -5.3198 },
  "melilla": { nombre: "Melilla", provincia: "Melilla", lat: 35.2923, lon: -2.9381 },
};

/**
 * Obtiene los datos de una capital por su slug
 * @param {string} slug - El slug URL-friendly de la capital
 * @returns {{ nombre: string, provincia: string } | null}
 */
export function getCapitalBySlug(slug) {
  if (!slug) return null;
  const normalized = slug.toLowerCase().trim();
  return CAPITALES_PROVINCIA[normalized] || null;
}

/**
 * Verifica si un slug corresponde a una capital de provincia
 * @param {string} slug
 * @returns {boolean}
 */
export function isCapitalProvincia(slug) {
  if (!slug) return false;
  return slug.toLowerCase().trim() in CAPITALES_PROVINCIA;
}

/**
 * Obtiene todos los slugs de las capitales de provincia
 * @returns {string[]}
 */
export function getAllCapitalesSlugs() {
  return Object.keys(CAPITALES_PROVINCIA);
}

/**
 * Obtiene todas las ubicaciones para el buscador del mapa
 * Incluye capitales de provincia con sus coordenadas
 * @returns {Array<{ title: string, value: string, lat: number, lon: number, type: string }>}
 */
export function getSearchLocations() {
  const locations = [];

  // Añadir capitales de provincia
  for (const [slug, data] of Object.entries(CAPITALES_PROVINCIA)) {
    locations.push({
      title: data.nombre,
      value: slug,
      lat: data.lat,
      lon: data.lon,
      type: 'capital',
      provincia: data.provincia
    });
  }

  // Ordenar alfabéticamente
  return locations.sort((a, b) => a.title.localeCompare(b.title, 'es'));
}